import React, { useEffect, useState } from "react";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { Checkbox, FormControlLabel, FormGroup, IconButton, Tooltip } from "@mui/material";
import Box from "@mui/material/Box";
import { DataGrid } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import Logger from "../../../services/Logger";
import ConfirmDialog from "../../components/ConfirmDialog";
import KeywordBox from "../../components/KeywordBox";
import { adminSlidingPlayDetailPath } from "../adminSlidingRoutes";
import AdminSlidingPlayService from "./AdminSlidingPlayService";
import { getSolveColumns } from "./getSolveColumns";

export default function AdminSlidingPlay({ slidingId }) {
    const navigate = useNavigate();
    const [pageModel, setPageModel] = useState({ page: 0, pageSize: 15 });
    const [sort, setSort] = useState([{ field: "startedAt", sort: "desc" }]);
    const [rowCount, setRowCount] = useState(0);
    const [rows, setRows] = useState([]);
    const [keyword, setKeyword] = useState("");
    const [completionStatus, setCompletionStatus] = useState({
        all: true,
        completed: false,
        incomplete: false,
    });
    const [onlyStarted, setOnlyStarted] = useState(false);
    const [selectedRowIds, setSelectedRowIds] = useState([]);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [, setSelectedRowDataForDelete] = useState([]);

    useEffect(() => {
        const isCompleted = completionStatus.completed
            ? true
            : completionStatus.incomplete
              ? false
              : null;
        AdminSlidingPlayService.requestPuzzlePlay(
            slidingId,
            keyword,
            pageModel,
            sort,
            setRows,
            setRowCount,
            isCompleted,
            onlyStarted,
        );
    }, [pageModel, sort, slidingId, keyword, completionStatus, onlyStarted]);

    const handleRowClick = (params, event) => {
        if (event.target.closest(".MuiCheckbox-root, .MuiDataGrid-cellCheckbox")) {
            return;
        }
        navigate(adminSlidingPlayDetailPath(params.row.id));
    };

    const handleSort = (model) => {
        setSort(model);
    };

    const handleSelectionModel = (newSelection) => {
        setSelectedRowIds(newSelection);
        const selectedRowData = rows.filter((row) => newSelection.includes(row.id));
        setSelectedRowDataForDelete(selectedRowData);
    };

    const handleCompletionStatusChange = (status) => {
        setCompletionStatus((prev) => {
            const newStatus = { ...prev };
            if (status === "all") {
                newStatus.all = true;
                newStatus.completed = false;
                newStatus.incomplete = false;
            } else {
                newStatus.all = false;
                newStatus[status] = !prev[status];
            }
            return newStatus;
        });
    };

    const handleDeleteClick = () => {
        if (selectedRowIds.length > 0) {
            setDeleteDialogOpen(true);
        }
    };

    const handleDeleteConfirm = async (targetIds) => {
        try {
            await AdminSlidingPlayService.deleteSlidingPlay(targetIds);
            const isCompleted = completionStatus.completed
                ? true
                : completionStatus.incomplete
                  ? false
                  : null;
            await AdminSlidingPlayService.requestPuzzlePlay(
                slidingId,
                keyword,
                pageModel,
                sort,
                setRows,
                setRowCount,
                isCompleted,
                onlyStarted,
            );
            setSelectedRowIds([]);
            setSelectedRowDataForDelete([]);
        } catch (error) {
            Logger.error("Error deleting sliding play:", error);
        }
    };

    const columns_solve = getSolveColumns();

    return (
        <Box sx={{ flexGrow: 1, height: "50%", display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                    <KeywordBox callback={setKeyword} />
                    <FormGroup row>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={completionStatus.all}
                                    onChange={() => handleCompletionStatusChange("all")}
                                    color="primary"
                                />
                            }
                            label="All"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={completionStatus.completed}
                                    onChange={() => handleCompletionStatusChange("completed")}
                                    color="primary"
                                />
                            }
                            label="Completed"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={completionStatus.incomplete}
                                    onChange={() => handleCompletionStatusChange("incomplete")}
                                    color="primary"
                                />
                            }
                            label="Incomplete"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={onlyStarted}
                                    onChange={(e) => setOnlyStarted(e.target.checked)}
                                    color="primary"
                                />
                            }
                            label="Only started"
                        />
                    </FormGroup>
                </Box>
                <Tooltip
                    title={`Delete ${selectedRowIds.length} selected row${
                        selectedRowIds.length !== 1 ? "s" : ""
                    }`}
                >
                    <span>
                        <IconButton
                            color="error"
                            onClick={handleDeleteClick}
                            disabled={selectedRowIds.length === 0}
                            sx={{ opacity: selectedRowIds.length > 0 ? 1 : 0.5 }}
                        >
                            <DeleteIcon />
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>
            <DataGrid
                rows={rows}
                columns={columns_solve}
                rowCount={rowCount}
                pageSizeOptions={[15, 25, 30]}
                paginationMode="server"
                sortingMode="server"
                pagination
                paginationModel={pageModel}
                onPaginationModelChange={(m) => setPageModel(m)}
                checkboxSelection
                experimentalFeatures={{ newEditingApi: false }}
                onSortModelChange={handleSort}
                onRowClick={handleRowClick}
                onRowSelectionModelChange={handleSelectionModel}
                rowSelectionModel={selectedRowIds}
                initialState={{
                    sorting: { sortModel: [{ field: "startedAt", sort: "desc" }] },
                }}
                sx={{ minHeight: 420, bgcolor: "background.paper" }}
            />
            <ConfirmDialog
                open={deleteDialogOpen}
                setOpen={setDeleteDialogOpen}
                title="Confirm Delete"
                message={`Are you sure you want to delete ${selectedRowIds.length} selected puzzle play record${
                    selectedRowIds.length !== 1 ? "s" : ""
                }? This action cannot be undone.`}
                warningAlert="Don't use for user's play history. For testing purpose only."
                targetId={selectedRowIds}
                callback={handleDeleteConfirm}
            />
        </Box>
    );
}
